import { __decorate } from "tslib";
import { ArrayDecoder, AutoEncoder, BooleanDecoder, DateDecoder, field, StringDecoder } from '@simonbackx/simple-encoding';
import { v4 as uuidv4 } from 'uuid';
import { Address } from './addresses/Address.js';
import { appToUri } from './AppType.js';
import { compileToInMemoryFilter } from './filters/InMemoryFilter.js';
import { organizationItemInMemoryFilterCompilers } from './filters/inMemoryFilterDefinitions.js';
import { Group } from './Group.js';
import { OrganizationMetaData } from './OrganizationMetaData.js';
import { OrganizationPrivateMetaData } from './OrganizationPrivateMetaData.js';
import { OrganizationRegistrationPeriod, RegistrationPeriod } from './RegistrationPeriod.js';
import { Webshop, WebshopPreview } from './webshops/Webshop.js';
import { Company } from './Company.js';
import { Language } from '@stamhoofd/types/Language';
export class BaseOrganization extends AutoEncoder {
    id;
    /**
     * Name of the organization you are creating
     */
    name = '';
    active = true;
    website = null;
    registerDomain = null;
    uri = '';
    meta;
    address = Address.create({});
    createdAt = new Date();
    /**
     * Return default locale confiruation
     */
    get i18n() {
        return { language: Language.Dutch, locale: 'nl-' + this.address.country };
    }
    /**
     * Potentially includes a path
     */
    getRegistrationHost(i18n) {
        if (this.registerDomain) {
            let d = this.registerDomain;
            if (i18n && i18n.language !== this.i18n.language) {
                d += '/' + i18n.language;
            }
            return d;
        }
        return this.getDefaultRegistrationHost(i18n);
    }
    getDefaultRegistrationHost(i18n) {
        if (!STAMHOOFD.domains.registration) {
            return STAMHOOFD.domains.dashboard + '/' + (i18n?.locale ?? this.i18n.locale) + '/' + appToUri('registration') + '/' + this.uri;
        }
        let defaultDomain = STAMHOOFD.domains.registration[this.address.country] ?? STAMHOOFD.domains.registration[''];
        if (i18n && i18n.language !== this.i18n.language) {
            defaultDomain += '/' + i18n.language;
        }
        return this.uri + '.' + defaultDomain;
    }
    /**
     * Used for redirecting
     */
    get resolvedRegisterDomain() {
        if (this.registerDomain) {
            return this.registerDomain;
        }
        if (!STAMHOOFD.domains.registration) {
            return null;
        }
        return this.uri + '.' + (STAMHOOFD.domains.registration[this.address.country] ?? STAMHOOFD.domains.registration['']);
    }
    get registerUrl() {
        return 'https://' + this.getRegistrationHost();
    }
    get dashboardUrl() {
        return 'https://' + STAMHOOFD.domains.dashboard + '/' + appToUri('dashboard') + '/' + this.uri;
    }
    get dashboardDomain() {
        return STAMHOOFD.domains.dashboard;
    }
    /**
     * Assures at least one company at all times
     */
    get defaultCompanies() {
        return this.meta.companies.length
            ? this.meta.companies
            : [
                Company.create({
                    name: this.name,
                    address: this.address,
                }),
            ];
    }
}
__decorate([
    field({ decoder: StringDecoder, defaultValue: () => uuidv4() })
], BaseOrganization.prototype, "id", void 0);
__decorate([
    field({ decoder: StringDecoder })
], BaseOrganization.prototype, "name", void 0);
__decorate([
    field({ decoder: BooleanDecoder, version: 325 })
], BaseOrganization.prototype, "active", void 0);
__decorate([
    field({ decoder: StringDecoder, nullable: true, version: 3, upgrade: () => null })
], BaseOrganization.prototype, "website", void 0);
__decorate([
    field({ decoder: StringDecoder, nullable: true, version: 3, upgrade: () => null })
], BaseOrganization.prototype, "registerDomain", void 0);
__decorate([
    field({ decoder: StringDecoder, version: 3, upgrade: () => '' })
], BaseOrganization.prototype, "uri", void 0);
__decorate([
    field({ decoder: OrganizationMetaData, defaultValue: () => OrganizationMetaData.create({}) })
], BaseOrganization.prototype, "meta", void 0);
__decorate([
    field({ decoder: Address })
], BaseOrganization.prototype, "address", void 0);
__decorate([
    field({ decoder: DateDecoder, version: 259 })
], BaseOrganization.prototype, "createdAt", void 0);
export class Organization extends BaseOrganization {
    /**
     * @deprecated
     * Please use period instead now
     */
    groups = [];
    period;
    /**
     * Get all groups that are in a category
     */
    get availableGroups() {
        return this.period.categoryTree.getAllGroups();
    }
    getGroupsForPermissions(permissions) {
        return this.getCategoryTree({ permissions }).getAllGroups();
    }
    get adminAvailableGroups() {
        return this.period.adminCategoryTree.getAllGroups();
    }
    /**
     * @deprecated
     *
     * Get all groups that are in a category
     */
    get availableCategories() {
        return this.period.availableCategories;
    }
    /**
     * @deprecated
     */
    get categoryTree() {
        return this.period.categoryTree;
    }
    /**
     * @deprecated
     */
    get publicCategoryTree() {
        return this.period.publicCategoryTree;
    }
    /**
     * @deprecated
     */
    get adminCategoryTree() {
        return this.getCategoryTree({ admin: true });
    }
    isAcceptingNewMembers(admin) {
        const allGroups = this.adminAvailableGroups; // we need to check admin groups because required groups could be restricted to internal groups
        const groups = this.getCategoryTree({ admin }).getAllGroups();
        for (const group of groups) {
            if (group.closed && !group.notYetOpen) {
                continue;
            }
            if (group.settings.requireGroupIds.length > 0 && group.settings.requireGroupIds.find(id => !!allGroups.find(g => g.id === id))) {
                continue;
            }
            if (group.settings.availableMembers === 0 && !group.waitingList) {
                continue;
            }
            return true;
        }
        return false;
    }
    isAcceptingExistingMembers(admin) {
        const groups = this.getCategoryTree({ admin }).getAllGroups();
        for (const group of groups) {
            if (group.closed) {
                continue;
            }
            if (group.settings.availableMembers === 0 && !group.waitingList) {
                continue;
            }
            return true;
        }
        return false;
    }
    /**
     * Contains the fully build hierarchy without the need for ID lookups. Try not to use this tree when modifying it.
     *
     * For registration members perspective, try to use options.admin instead of options.permissions.
     * options.permissions is only used if you want to hide groups and empty categories that you don't have permissions for.
     */
    getCategoryTree(options) {
        return this.period.getCategoryTree(options ? { ...options, organization: this } : { organization: this });
    }
    /**
     * @deprecated
     * (todo) Contains the fully build hierarchy without the need for ID lookups. Try not to use this tree when modifying it.
     */
    getCategoryTreeWithDepth(maxDepth) {
        return this.getCategoryTree({ maxDepth });
    }
    /**
     * Only set for users with full access to the organization
     */
    privateMeta = null;
    /**
     * Only set for users with full access to the organization
     */
    webshops = [];
    /**
     * Only available for patching. Also available with lazy loading OrganizationAdmins
     */
    admins = null;
    /**
     * Keep admins accessible and in memory
     */
    periods;
    isRecordEnabled(_record) {
        return true;
    }
    getRecordAnswers() {
        return this.privateMeta?.recordAnswers ?? new Map();
    }
    patchRecordAnswers(patch) {
        return this.patch({
            privateMeta: OrganizationPrivateMetaData.patch({
                recordAnswers: patch,
            }),
        });
    }
    doesMatchFilter(filter) {
        try {
            const compiledFilter = compileToInMemoryFilter(filter, organizationItemInMemoryFilterCompilers);
            const value = compiledFilter(this);
            console.log('does match filter', filter, this, value);
            return value;
        }
        catch (e) {
            console.error('Error while compiling filter', e, filter);
        }
        return false;
    }
    updatePeriods(periods) {
        // Update in memory
        for (const period of this.periods?.organizationPeriods ?? []) {
            const updated = periods.find(p => p.id === period.id);
            if (updated) {
                period.deepSet(updated);
            }
        }
        const updated = periods.find(p => p.id === this.period.id);
        if (updated) {
            this.period.deepSet(updated);
        }
        // Add missing periods
    }
}
__decorate([
    field({ decoder: new ArrayDecoder(Group), version: 2, upgrade: () => [], optional: true })
], Organization.prototype, "groups", void 0);
__decorate([
    field({ decoder: OrganizationRegistrationPeriod, version: 264, defaultValue: () => OrganizationRegistrationPeriod.create({ period: RegistrationPeriod.create({}) }) })
], Organization.prototype, "period", void 0);
__decorate([
    field({ decoder: OrganizationPrivateMetaData, nullable: true, version: 6 })
], Organization.prototype, "privateMeta", void 0);
__decorate([
    field({ decoder: new ArrayDecoder(WebshopPreview), version: 38, upgrade: () => [] })
], Organization.prototype, "webshops", void 0);
export class OrganizationWithWebshop extends AutoEncoder {
    organization;
    webshop;
}
__decorate([
    field({ decoder: Organization })
], OrganizationWithWebshop.prototype, "organization", void 0);
__decorate([
    field({ decoder: Webshop })
], OrganizationWithWebshop.prototype, "webshop", void 0);
export class GetWebshopFromDomainResult extends AutoEncoder {
    organization;
    /**
     * There is one specific webshop for the given domain.
     */
    webshop = null;
    /**
     * Multiple webshops possible. Show a selection page.
     */
    webshops = [];
}
__decorate([
    field({ decoder: Organization })
], GetWebshopFromDomainResult.prototype, "organization", void 0);
__decorate([
    field({ decoder: Webshop, nullable: true })
], GetWebshopFromDomainResult.prototype, "webshop", void 0);
__decorate([
    field({ decoder: new ArrayDecoder(WebshopPreview), version: 137 })
], GetWebshopFromDomainResult.prototype, "webshops", void 0);
//# sourceMappingURL=Organization.js.map